import type { CreateFormData } from "@/components/create/types";
import type { DesignBrief, DressConcept } from "@/types/createAgent";
import type { DesignSpec } from "@/types/designSpec";
import { designSpecSchema } from "@/types/designSpec";

/** Restore editable values, including explicit clears, from a saved render. */
export function restoreDesignDraft(spec: DesignSpec) {
  spec = designSpecSchema.parse(spec);
  const visual = spec.visual;
  const formData: Partial<CreateFormData> = {
    prompt: visual.prompt ?? "",
    preset_type: spec.occasion ?? "",
    color: visual.primaryColor ?? "",
    colorHex: "",
    length: visual.length ?? "",
    sleeve: visual.sleeve ?? "",
    sleeveAddons: [...visual.sleeveAddons],
    neckline: visual.neckline ?? "",
    waistline: visual.waistline ?? "",
    silhouette: visual.silhouette ?? "",
    fabric: visual.fabric ?? "",
    backPrompt: visual.backPrompt ?? "",
    modestyPreference: spec.constraints.modesty,
  };
  const brief: DesignBrief = {
    version: 2,
    userGoal: visual.prompt,
    occasion: spec.occasion,
    event: { date: spec.eventDate, wearerContext: [...spec.wearerContext] },
    front: {
      prompt: visual.prompt,
      color: visual.primaryColor,
      length: visual.length,
      sleeve: visual.sleeve,
      sleeveAddons: [...visual.sleeveAddons],
      neckline: visual.neckline,
      waistline: visual.waistline,
      silhouette: visual.silhouette,
      fabric: visual.fabric,
      mood: [...visual.mood],
      details: [...visual.details],
    },
    back: {
      prompt: visual.backPrompt,
      style: visual.backStyle,
      closure: visual.closure,
      details: visual.backPrompt ? [visual.backPrompt] : [],
    },
    constraints: {
      ...spec.constraints,
      referenceGuidance: [...spec.referenceGuidance],
      color: {
        primary: visual.primaryColor,
        accents: [...visual.accentColors],
        preferred: visual.primaryColor ? [visual.primaryColor] : [],
        rejected: [],
      },
    },
    // Explicit roles preserve reference order and instructions without adding
    // fallback references from the discarded draft's uploads.
    references: {
      front: [],
      back: [],
      inspiration: [],
      roles: spec.references,
    },
    rejectedIdeas: [...spec.constraints.avoid],
    openQuestions: [],
    pendingAction: { type: "none" },
    provenance: spec.provenance,
  };
  return { formData, brief };
}

export function restoreConceptDecisions(
  concept: DressConcept | null | undefined,
  spec: DesignSpec
) {
  if (!concept) return concept;
  return {
    ...concept,
    visualDecisions: {
      ...concept.visualDecisions,
      color: spec.visual.primaryColor,
      length: spec.visual.length,
      sleeve: spec.visual.sleeve,
      neckline: spec.visual.neckline,
      waistline: spec.visual.waistline,
      silhouette: spec.visual.silhouette,
      fabric: spec.visual.fabric,
      back: spec.visual.backPrompt,
      details: [...spec.visual.details],
    },
  };
}
